import { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route } from 'react-router-dom';
import { AppHeader } from '@/components/layout/AppHeader';
import { AppSidebar } from '@/components/layout/AppSidebar';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { formatWorkspaceRole } from '@/lib/workspaceRoles';
import { formatDateTime, formatRelativeTime, getErrorMessage } from '@/lib/utils';
import { useWorkspace } from '@/lib/contexts/useWorkspace';
import { workspaceService, type TeamMember, type PendingInvitation } from '@/lib/services/workspaceService';
import { 
  PlusIcon,
  MagnifyingGlassIcon,
  EnvelopeIcon,
  UserCircleIcon,
  EllipsisVerticalIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

// TeamMember and PendingInvitation interfaces are now imported from workspaceService

// Confirmation dialog types
interface ConfirmDialogState {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText: string;
  confirmVariant: 'danger' | 'warning' | 'primary';
  onConfirm: () => void;
}

function TeamList() {
  const [searchTerm, setSearchTerm] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isInviting, setIsInviting] = useState(false);
  const [isCancelling, setIsCancelling] = useState<string | null>(null); // Track which invitation is being cancelled
  const [isUpdatingRole, setIsUpdatingRole] = useState<string | null>(null); // Track which member's role is being updated
  const [isRemovingMember, setIsRemovingMember] = useState<string | null>(null); // Track which member is being removed
  const [openDropdown, setOpenDropdown] = useState<string | null>(null); // Track which dropdown is open
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
  const dropdownContainerRef = useRef<HTMLDivElement | null>(null);
  
  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    confirmVariant: 'danger',
    onConfirm: () => {},
  });
  
  // Form state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'viewer' | 'editor' | 'owner'>('viewer');
  const [personalMessage, setPersonalMessage] = useState('');

  const { currentWorkspace } = useWorkspace();

  const loadTeamData = useCallback(async () => {
    if (!currentWorkspace) return;
    
    setIsLoading(true);
    try {
      const teamData = await workspaceService.getWorkspaceMembers(currentWorkspace.workspaceId);
      
      setMembers(teamData.members);
      setPendingInvitations(teamData.pendingInvitations);
    } catch (error: unknown) {
      console.error('Error loading team data:', error);
      toast.error('Failed to load team data');
      
      // Set empty arrays on error
      setMembers([]);
      setPendingInvitations([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentWorkspace]);

  // Load workspace members and pending invitations
  useEffect(() => {
    if (currentWorkspace) {
      void loadTeamData();
    }
  }, [currentWorkspace, loadTeamData]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        openDropdown &&
        dropdownContainerRef.current &&
        !dropdownContainerRef.current.contains(event.target as Node)
      ) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  const handleInviteMember = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentWorkspace) {
      toast.error('No workspace selected');
      return;
    }

    if (!inviteEmail.trim()) {
      toast.error('Email is required');
      return;
    }

    setIsInviting(true);
    try {
      await workspaceService.inviteMember(
        currentWorkspace.workspaceId, 
        inviteEmail.trim(), 
        inviteRole, 
        personalMessage.trim() || undefined
      );
      
      toast.success('Invitation sent successfully!');
      
      // Reset form
      setInviteEmail('');
      setInviteRole('viewer');
      setPersonalMessage('');
      setShowInviteModal(false);
      
      // Reload team data
      await loadTeamData();
    } catch (error: unknown) {
      console.error('Error inviting member:', error);
      toast.error(getErrorMessage(error) || 'Failed to send invitation');
    } finally {
      setIsInviting(false);
    }
  };

  const showConfirmDialog = (
    title: string,
    message: string,
    confirmText: string,
    confirmVariant: 'danger' | 'warning' | 'primary',
    onConfirm: () => void
  ) => {
    setConfirmDialog({
      isOpen: true,
      title,
      message,
      confirmText,
      confirmVariant,
      onConfirm,
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog(prev => ({ ...prev, isOpen: false }));
  };

  const handleCancelInvitation = (invitationId: string, email: string) => {
    if (!currentWorkspace) {
      toast.error('No workspace selected');
      return;
    }

    showConfirmDialog(
      'Cancel Invitation',
      `Are you sure you want to cancel the invitation for ${email}? This action cannot be undone.`,
      'Cancel Invitation',
      'danger',
      async () => {
        closeConfirmDialog();
        setIsCancelling(invitationId);
        try {
          await workspaceService.cancelInvitation(currentWorkspace.workspaceId, invitationId);
          toast.success('Invitation cancelled successfully!');
          await loadTeamData();
        } catch (error: unknown) {
          console.error('Error cancelling invitation:', error);
          toast.error(getErrorMessage(error) || 'Failed to cancel invitation');
        } finally {
          setIsCancelling(null);
        }
      }
    );
  };

  const handleUpdateRole = (userId: string, email: string, currentRole: string, newRole: 'owner' | 'editor' | 'viewer') => {
    if (!currentWorkspace) {
      toast.error('No workspace selected');
      return;
    }

    showConfirmDialog(
      'Change Role',
      `Are you sure you want to change ${email}'s role from ${currentRole} to ${newRole}?`,
      'Change Role',
      'warning',
      async () => {
        closeConfirmDialog();
        setIsUpdatingRole(userId);
        try {
          await workspaceService.updateMemberRole(currentWorkspace.workspaceId, userId, newRole);
          toast.success(`Role updated to ${newRole} successfully!`);
          await loadTeamData();
        } catch (error: unknown) {
          console.error('Error updating role:', error);
          toast.error(getErrorMessage(error) || 'Failed to update role');
        } finally {
          setIsUpdatingRole(null);
        }
      }
    );
  };

  const handleRemoveMember = (userId: string, email: string) => {
    if (!currentWorkspace) {
      toast.error('No workspace selected');
      return;
    }

    showConfirmDialog(
      'Remove Member',
      `Are you sure you want to remove ${email} from this workspace? They will lose access to all workspace resources. This action cannot be undone.`,
      'Remove Member',
      'danger',
      async () => {
        closeConfirmDialog();
        setIsRemovingMember(userId);
        try {
          await workspaceService.removeMember(currentWorkspace.workspaceId, userId);
          toast.success('Member removed successfully!');
          await loadTeamData();
        } catch (error: unknown) {
          console.error('Error removing member:', error);
          toast.error(getErrorMessage(error) || 'Failed to remove member');
        } finally {
          setIsRemovingMember(null);
        }
      }
    );
  };

  // Filter both members and pending invitations
  const allTeamData = [
    ...members.map(m => ({ ...m, type: 'member' as const })),
    ...pendingInvitations.map(p => ({ ...p, type: 'pending' as const }))
  ];

  const filteredTeamData = allTeamData.filter(item => {
    const searchLower = searchTerm.toLowerCase();
    return (
      item.email.toLowerCase().includes(searchLower) ||
      ('name' in item && item.name?.toLowerCase().includes(searchLower)) ||
      item.role.toLowerCase().includes(searchLower)
    );
  });

  const getInitials = (name: string, email: string) => {
    if (name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase();
    }
    return email.split('@')[0].slice(0, 2).toUpperCase();
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner': return 'bg-purple-100 text-purple-700';
      case 'editor': return 'bg-blue-100 text-blue-700';
      case 'viewer': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const isInvitationExpired = (expiresAt: string) => {
    return new Date(expiresAt) < new Date();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <AppHeader />
        <div className="flex flex-1">
          <AppSidebar />
          <main className="flex-1 py-8">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header - full width at top */}
      <AppHeader />
      
      {/* Content area with sidebar */}
      <div className="flex flex-1">
        <AppSidebar />
        
        {/* Main content */}
        <main className="flex-1 py-8">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Workspace Members</h1>
                  <p className="mt-2 text-gray-600">
                    {currentWorkspace ? (
                      <>
                        {currentWorkspace.name} • {members.length} members
                        {pendingInvitations.length > 0 && `, ${pendingInvitations.length} pending`}
                      </>
                    ) : (
                      'Manage who can access the active workspace and what they can do inside it.'
                    )}
                  </p>
                </div>
                <Button
                  onClick={() => setShowInviteModal(true)}
                  className="inline-flex items-center"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Invite Members
                </Button>
              </div>
            </div>

            {/* Search */}
            <div className="mb-6">
              <div className="relative max-w-md">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search members..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Members Grid */}
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredTeamData.map((item) => (
                <div key={item.type === 'member' ? item.userId : item.invitationId} 
                     className={`bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow flex flex-col h-full ${
                       item.type === 'pending' ? 'border-l-4 border-l-orange-400' : ''
                     }`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <div className={`h-12 w-12 rounded-full flex items-center justify-center ${
                        item.type === 'pending' ? 'bg-orange-100' : 'bg-primary/10'
                      }`}>
                        {item.type === 'pending' ? (
                          <ClockIcon className="h-6 w-6 text-orange-600" />
                        ) : (
                          <span className="text-primary font-medium text-lg">
                            {getInitials('name' in item ? item.name || '' : '', item.email)}
                          </span>
                        )}
                      </div>
                      <div className="ml-3">
                        <h3 className="font-semibold text-gray-900">
                          {'name' in item ? item.name || item.email.split('@')[0] : item.email.split('@')[0]}
                        </h3>
                        <p className="text-sm text-gray-600">{formatWorkspaceRole(item.role)}</p>
                      </div>
                    </div>
                    <button className="text-gray-400 hover:text-gray-600">
                      <EllipsisVerticalIcon className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="space-y-3 flex-grow">
                    <div className="flex items-center text-sm">
                      <EnvelopeIcon className="h-4 w-4 text-gray-400 mr-2" />
                      <span className="text-gray-600">{item.email}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Status:</span>
                      <div className="flex items-center">
                        {item.type === 'pending' ? (
                          <>
                            <ClockIcon className="h-4 w-4 text-orange-500 mr-1" />
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              isInvitationExpired(item.expiresAt) 
                                ? 'bg-red-100 text-red-700' 
                                : 'bg-orange-100 text-orange-700'
                            }`}>
                              {isInvitationExpired(item.expiresAt) ? 'Expired' : 'Pending'}
                            </span>
                          </>
                        ) : (
                          <>
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" />
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                              Active
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">
                        {item.type === 'pending' ? 'Invited:' : 'Joined:'}
                      </span>
                      <span className="text-sm text-gray-900">
                        <span title={formatDateTime(item.type === 'pending' ? item.invitedAt : item.joinedAt)}>
                          {formatRelativeTime(item.type === 'pending' ? item.invitedAt : item.joinedAt)}
                        </span>
                      </span>
                    </div>

                    {item.type === 'pending' && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">Expires:</span>
                        <span className={`text-sm ${
                          isInvitationExpired(item.expiresAt) ? 'text-red-600' : 'text-gray-900'
                        }`}>
                          <span title={formatDateTime(item.expiresAt)}>
                            {formatRelativeTime(item.expiresAt)}
                          </span>
                        </span>
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Role:</span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(item.role)}`}>
                        {formatWorkspaceRole(item.role)}
                      </span>
                    </div>
                  </div>

                  <div className="mt-auto pt-4 border-t border-gray-100 flex items-center justify-between">
                    {item.type === 'pending' ? (
                      <>
                        <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                          Resend Invite
                        </button>
                        <button 
                          onClick={() => handleCancelInvitation(item.invitationId, item.email)}
                          disabled={isCancelling === item.invitationId}
                          className="text-red-600 hover:text-red-800 text-sm inline-flex items-center disabled:opacity-50"
                        >
                          {isCancelling === item.invitationId ? (
                            <>
                              <LoadingSpinner size="sm" className="mr-1" />
                              Cancelling...
                            </>
                          ) : (
                            'Cancel'
                          )}
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <div
                            className="relative"
                            ref={openDropdown === item.userId ? dropdownContainerRef : null}
                          >
                            <button
                              onClick={() => setOpenDropdown(openDropdown === item.userId ? null : item.userId)}
                              disabled={isUpdatingRole === item.userId}
                              className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed w-24 text-left flex items-center justify-between"
                            >
                              <span>{formatWorkspaceRole(item.role)}</span>
                              <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                            </button>
                            
                            {openDropdown === item.userId && (
                              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
                                <button
                                  onClick={() => {
                                    handleUpdateRole(item.userId, item.email, item.role, 'viewer');
                                    setOpenDropdown(null);
                                  }}
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 first:rounded-t-md"
                                >
                                  Viewer
                                </button>
                                <button
                                  onClick={() => {
                                    handleUpdateRole(item.userId, item.email, item.role, 'editor');
                                    setOpenDropdown(null);
                                  }}
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-t border-gray-100"
                                >
                                  Editor
                                </button>
                                {/* Only show owner option if user is already an owner */}
                                {item.role === 'owner' && (
                                  <button
                                    onClick={() => {
                                      handleUpdateRole(item.userId, item.email, item.role, 'owner');
                                      setOpenDropdown(null);
                                    }}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-t border-gray-100 last:rounded-b-md"
                                  >
                                    Owner
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                          {isUpdatingRole === item.userId && (
                            <LoadingSpinner size="sm" />
                          )}
                        </div>
                        <button 
                          onClick={() => handleRemoveMember(item.userId, item.email)}
                          disabled={isRemovingMember === item.userId || item.role === 'owner'}
                          className="text-red-600 hover:text-red-800 text-sm inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                          title={item.role === 'owner' ? 'Cannot remove workspace owner' : 'Remove member'}
                        >
                          {isRemovingMember === item.userId ? (
                            <>
                              <LoadingSpinner size="sm" className="mr-1" />
                              Removing...
                            </>
                          ) : (
                            'Remove'
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Empty State */}
            {filteredTeamData.length === 0 && (
              <div className="text-center py-12">
                <UserCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-semibold text-gray-900">No members found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm 
                    ? 'Try adjusting your search criteria.'
                    : 'Get started by inviting the first member into this workspace.'
                  }
                </p>
                {!searchTerm && (
                  <div className="mt-6">
                    <Button
                      onClick={() => setShowInviteModal(true)}
                      className="inline-flex items-center"
                    >
                      <PlusIcon className="h-5 w-5 mr-2" />
                      Invite Members
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Invite Workspace Members</h3>
            
            <form onSubmit={handleInviteMember} className="space-y-4">
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  type="email"
                  id="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="Enter email address"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="role">Role</Label>
                <select
                  id="role"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as 'viewer' | 'editor' | 'owner')}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="viewer">Viewer</option>
                  <option value="editor">Editor</option>
                  <option value="owner">Owner</option>
                </select>
              </div>
              
              <div>
                <Label htmlFor="message">Personal Message (Optional)</Label>
                <textarea
                  id="message"
                  rows={3}
                  value={personalMessage}
                  onChange={(e) => setPersonalMessage(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Add a personal message to the invitation"
                />
              </div>

              <div className="flex items-center justify-end space-x-3 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowInviteModal(false)}
                  disabled={isInviting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isInviting}
                  className="inline-flex items-center"
                >
                  {isInviting && <LoadingSpinner size="sm" className="mr-2" />}
                  Send Invite
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmDialog.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
            <div className="flex items-start gap-4">
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                confirmDialog.confirmVariant === 'danger' 
                  ? 'bg-red-100' 
                  : confirmDialog.confirmVariant === 'warning'
                  ? 'bg-amber-100'
                  : 'bg-blue-100'
              }`}>
                <ExclamationTriangleIcon className={`h-5 w-5 ${
                  confirmDialog.confirmVariant === 'danger' 
                    ? 'text-red-600' 
                    : confirmDialog.confirmVariant === 'warning'
                    ? 'text-amber-600'
                    : 'text-blue-600'
                }`} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">
                  {confirmDialog.title}
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  {confirmDialog.message}
                </p>
              </div>
            </div>
            
            <div className="flex items-center justify-end space-x-3 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={closeConfirmDialog}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={confirmDialog.onConfirm}
                className={
                  confirmDialog.confirmVariant === 'danger'
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : confirmDialog.confirmVariant === 'warning'
                    ? 'bg-amber-600 hover:bg-amber-700 text-white'
                    : ''
                }
              >
                {confirmDialog.confirmText}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TeamPage() {
  return (
    <Routes>
      <Route path="/" element={<TeamList />} />
    </Routes>
  );
}
